import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { FileSearch, Download, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const d10 = (v) => (v ? `${v.slice(8, 10)}.${v.slice(5, 7)}.${v.slice(0, 4)}` : "—");

const Chronologie = ({ nr }) => {
  const [c, setC] = useState(null);
  if (!nr || !/^\d{4}\/\d+/.test(nr)) return null;
  const laden = async (umfang) => setC((await axios.get(`${API}/zertifikat-chronologie`, { params: { nr, umfang } })).data);
  if (!c) return <Button size="sm" variant="ghost" onClick={() => laden(12)} data-testid="beleg-check-chronologie-btn">Nummern-Chronologie prüfen</Button>;
  return (
    <div className="mt-2 text-xs" data-testid="beleg-check-chronologie">
      <p className="text-stone-500 mb-1">{c.anzahl_im_jahr} Zertifikate in {c.jahr} (Nr. {c.kleinste} bis {c.groesste}) · CRM-Zähler: {c.zaehler?.jahr}/{c.zaehler?.letzte_nr ?? "—"} · ohne Druck im CRM: {c.ohne_crm_druck?.length || 0}
        {" "}<button className="underline" onClick={() => laden(100000)} data-testid="beleg-check-chronologie-alle">alle anzeigen</button></p>
      <table className="w-full">
        <thead><tr className="text-left text-stone-500"><th>Nr.</th><th>Name</th><th>Datum auf Zertifikat</th><th>Im CRM erzeugt</th><th>Hinweis</th></tr></thead>
        <tbody>
          {c.nachbarn.map((e) => (
            <tr key={e.nr} className={e.nr === c.nr ? "font-semibold" : ""}>
              <td>{e.nr}</td><td>{e.name}</td><td>{d10(e.ausgestellt_am)}</td><td>{d10(e.im_crm_erzeugt_am)}</td>
              <td className={e.auffaellig ? "text-red-700" : "text-stone-400"}>
                {e.nummer_passt_nicht_zum_datum ? "Datum passt nicht zur Nummernfolge" : ""}{e.nummer_passt_nicht_zum_datum && e.spaeter_erzeugt_tage > 14 ? " · " : ""}{e.spaeter_erzeugt_tage > 14 ? `${e.spaeter_erzeugt_tage} Tage später erzeugt` : ""}{!e.auffaellig ? "ok" : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Unterschriften = ({ t, idx }) => {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (dry_run) => {
    if (!dry_run && !window.confirm(`Original-Unterschrift vom ${d10(String(b.unterschrieben_am).slice(0, 10))} in ${b.dokumente.join(", ")} wiederherstellen? Alte Dateien werden archiviert.`)) return;
    setBusy(true);
    try { setB((await axios.post(`${API}/admin/unterschriften-wiederherstellen`, { teilnehmer_id: t.teilnehmer_id, dry_run })).data); }
    finally { setBusy(false); }
  };
  if (!b) return <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(true)} data-testid={`beleg-check-sig-pruefen-${idx}`}>Unterschriften prüfen</Button>;
  return (
    <div className="mt-2 text-xs border-t pt-2" data-testid={`beleg-check-sig-bericht-${idx}`}>
      <p>Zur Unterschrift versendet: <b>{d10(String(b.zur_unterschrift_versendet || "").slice(0, 10))}</b> · unterschrieben: <b>{d10(String(b.unterschrieben_am || "").slice(0, 10))}</b> · Maßnahmebeginn {d10(b.massnahmebeginn)} · Quelle: {b.unterschrift_quelle || "—"}</p>
      {b.unterschrieben_nach_massnahmebeginn && <p className="text-red-700" data-testid={`beleg-check-sig-spaet-${idx}`}>Achtung: laut Protokoll NACH Maßnahmebeginn unterschrieben.</p>}
      {b.fehler && <p className="text-red-700" data-testid={`beleg-check-sig-fehler-${idx}`}>{b.fehler}</p>}
      {b.wiederhergestellt && <p className="text-emerald-700" data-testid={`beleg-check-sig-ok-${idx}`}>Wiederhergestellt: {b.wiederhergestellt.join(", ")}{b.fehler_liste?.length ? ` · Fehler: ${b.fehler_liste.map((f) => f.dokument).join(", ")}` : ""}</p>}
      {b.dry_run && !b.fehler && <Button size="sm" className="mt-1 bg-[#D9775A] hover:bg-[#C2603F] text-white" disabled={busy} onClick={() => run(false)} data-testid={`beleg-check-sig-wiederherstellen-${idx}`}>Original-Unterschrift in {b.dokumente.length} Dokumente wiederherstellen</Button>}
    </div>
  );
};

const Treffer = ({ t, idx, onApv }) => (
  <div className="border rounded p-3 mb-3" data-testid={`beleg-check-treffer-${idx}`}>
    <div className="font-medium">{t.name} · {t.kurs?.kurs_name || "—"} · Zert.-Nr. {t.zertifikatsnummer || "—"}</div>
    <p className="text-xs text-stone-500 mb-1" data-testid={`beleg-check-meta-${idx}`}>Kd.-Nr. {t.kundennummer || "—"} · BG-Nr. {t.bildungsgutschein_nummer || "—"} · BG von {t.bg_stelle || "—"} · Wohnort {t.wohnort || "—"}</p>
    <table className="w-full text-sm">
      <tbody>
        {t.checks.map((c, i) => (
          <tr key={i} className="border-b last:border-0" data-testid={`beleg-check-punkt-${idx}-${i}`}>
            <td className="py-1 w-1/3">{c.punkt}</td>
            <td className="w-24">
              <span className={`text-xs px-2 py-0.5 rounded ${c.ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{c.ok ? "vorhanden" : "FEHLT"}</span>
            </td>
            <td className="text-stone-600 break-all">{c.info || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className="flex items-center justify-between mt-2">
      <p className="text-xs text-stone-500">{t.ausstellungen.length} Ausstellungen · {t.emails.length} E-Mails · {t.anhaenge.length} Uploads</p>
      <Button size="sm" variant="outline" onClick={() => onApv(t)} data-testid={`beleg-check-apv-${idx}`}><FileText size={14} className="mr-1" /> APV-Antwortschreiben mit diesem Fall</Button>
    </div>
    {t.ersetzte_zertifikat_dateien?.length > 0 && (
      <div className="mt-2 text-xs text-stone-600" data-testid={`beleg-check-ersetzt-${idx}`}>
        <p className="font-medium">Frühere Zertifikat-Dateien (ausgeblendet):</p>
        {t.ersetzte_zertifikat_dateien.map((f, i) => (
          <p key={i}>{f.filename} · erzeugt/hochgeladen {d10(String(f.uploaded_at || "").slice(0, 10))}{f.gedruckt_von_name || f.uploaded_by ? ` von ${f.gedruckt_von_name || f.uploaded_by}` : ""} · ausgeblendet {d10(String(f.deleted_at || "").slice(0, 10))}</p>
        ))}
      </div>
    )}
    <Unterschriften t={t} idx={idx} />
    <Chronologie nr={t.zertifikatsnummer} />
  </div>
);

export const ZertifikatBelegCheck = () => {
  const [nr, setNr] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const pruefen = async () => {
    if (!nr.trim()) return;
    setLoading(true);
    try { setData((await axios.get(`${API}/zertifikat-beleg-check`, { params: { nr: nr.trim() } })).data); }
    finally { setLoading(false); }
  };

  const pdf = async () => {
    const r = await axios.get(`${API}/zertifikat-beleg-check-pdf`, { params: { nr: nr.trim() }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `Beleg-Check_${nr.trim().replace(/\W/g, "-")}.pdf`; a.click(); URL.revokeObjectURL(url);
  };

  const apv = async (t) => {
    const params = { fall_nr: data.zertifikatsnummer, fall_tn: t.teilnehmer_id };
    const chk = (await axios.get(`${API}/azav/apv-antwortschreiben-check`, { params })).data;
    if (chk.fehlend?.length) { window.alert(`Bitte zuerst in der Akte nachtragen: ${chk.fehlend.join(", ")}.\nDas Schreiben wird nur mit vollständigen Angaben erstellt.`); return; }
    const r = await axios.get(`${API}/azav/apv-antwortschreiben-pdf`, { params, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = "Antwort_APV_Korrektur_MN-364-1232-2024.pdf"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <Card className="card-custom mb-6" data-testid="beleg-check-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><FileSearch size={18} /> Beleg-Check Zertifikat / Bildungsgutschein</CardTitle>
        <div className="flex gap-2 mt-2">
          <Input value={nr} onChange={(e) => setNr(e.target.value)} onKeyDown={(e) => e.key === "Enter" && pruefen()}
            placeholder="Zertifikats-, Kunden- oder BG-Nr., Name oder Ort" className="max-w-md" data-testid="beleg-check-input" />
          <Button onClick={pruefen} disabled={loading} className="bg-[#D9775A] hover:bg-[#C2603F] text-white" data-testid="beleg-check-submit">Prüfen</Button>
          {data?.gefunden && <Button variant="outline" onClick={pdf} data-testid="beleg-check-pdf"><Download size={14} className="mr-1" /> Belege als PDF</Button>}
        </div>
      </CardHeader>
      {data && (
        <CardContent className="pt-0" data-testid="beleg-check-result">
          {!data.gefunden
            ? <p className="text-sm text-red-700" data-testid="beleg-check-nicht-gefunden">Zu „{data.zertifikatsnummer}" wurde im CRM nichts gefunden.</p>
            : data.treffer.map((t, i) => <Treffer key={t.teilnehmer_id} t={t} idx={i} onApv={apv} />)}
        </CardContent>
      )}
    </Card>
  );
};
